/**
 * Utilitaires de formatage pour l'application
 */

/**
 * Formate une date en format lisible
 * @param {string|Date} date - La date à formater
 * @param {Object} options - Options de formatage
 * @returns {string} - Date formatée
 */
export const formatDate = (date, options = {}) => {
  const dateObj = new Date(date);

  if (isNaN(dateObj.getTime())) {
    return "Date invalide";
  }

  const defaultOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  };

  return new Intl.DateTimeFormat("fr-FR", defaultOptions).format(dateObj);
};

/**
 * Formate un montant en devise
 * @param {number} amount - Montant à formater
 * @param {string} currency - Code de la devise (par défaut: EUR)
 * @param {Object} options - Options de formatage
 * @returns {string} - Montant formaté
 */
export const formatCurrency = (amount, currency = "USD") => {
  // Gérer les valeurs null, undefined ou NaN
  if (amount === null || amount === undefined || isNaN(amount)) {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0);
  }
  
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Formate un numéro de téléphone
 * @param {string} phoneNumber - Numéro de téléphone à formater
 * @returns {string} - Numéro formaté
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";

  // Supprimer tous les caractères non numériques
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Formater selon différents formats en fonction du pays
  if (cleaned.startsWith("7") && cleaned.length === 10) {
    // Format russe: +7 XXX XXX-XX-XX
    return `+7 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  } else if (cleaned.startsWith("221") && cleaned.length >= 11) {
    // Format sénégalais: +221 XX XXX XX XX
    return `+221 ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 10)} ${cleaned.slice(10)}`;
  }

  // Format générique si aucun format spécifique ne correspond
  return `+${cleaned}`;
};

/**
 * Tronque un texte à une longueur spécifiée
 * @param {string} text - Texte à tronquer
 * @param {number} length - Longueur maximale
 * @param {string} suffix - Suffixe à ajouter en cas de troncature
 * @returns {string} - Texte tronqué
 */
export const truncateText = (text, length = 50, suffix = "...") => {
  if (!text) return "";
  if (text.length <= length) return text;
  return text.substring(0, length).trim() + suffix;
};

/**
 * Formate un identifiant blockchain
 * @param {string} hash - Hash à formater
 * @param {number} start - Nombre de caractères à garder au début
 * @param {number} end - Nombre de caractères à garder à la fin
 * @returns {string} - Hash formaté
 */
export const formatHash = (hash, start = 6, end = 4) => {
  if (!hash || hash.length <= start + end) return hash;
  return `${hash.substring(0, start)}...${hash.substring(hash.length - end)}`;
};

/**
 * Formate de grands nombres en utilisant des suffixes (K, M, B).
 * Idéal pour les montants de tokens, market caps, etc.
 * @param {number} num - Le nombre à formater.
 * @param {number} [digits=2] - Le nombre de décimales à conserver après le formatage.
 * @returns {string} Le nombre formaté sous forme de chaîne.
 */
export const formatTokenAmount = (num, digits = 2) => {
  if (num === null || num === undefined) return "N/A";
  if (num === 0) return "0";

  const si = [
    { value: 1, symbol: "" },
    { value: 1E3, symbol: "K" },
    { value: 1E6, symbol: "M" },
    { value: 1E9, symbol: "B" },
    { value: 1E12, symbol: "T" },
  ];

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = si.slice().reverse().find(item => num >= item.value);

  if (!item) {
    return num.toPrecision(digits);
  }

  const formattedNum = (num / item.value).toFixed(digits).replace(rx, "$1");
  return formattedNum + item.symbol;
};
